import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import Dropdown from 'react-bootstrap/lib/Dropdown';
import MenuItem from 'react-bootstrap/lib/MenuItem';
import { injectIntl } from 'react-intl';
import { MdForwardToInbox, MdOpenInNew } from 'react-icons/md';
import { FaFilePdf } from 'react-icons/fa';
import intlShape from '@/shared/utils/intlShape';
import DocumentService from '@/shared/services/DocumentService';
import {
  DeleteIcon,
  IconPencil,
  PaperPlaneIcon,
  ShareIcon,
  ToDoIcon,
  UndoIcon,
  MailIcon,
  SpinnerIcon,
  ReplaceIcon,
  SaveIcon,
  SharepointIcon,
} from '@/shared/components/Icons';
import authService from '@/shared/services/AuthService';
import dataCacheService from '@/shared/services/DataCacheService';
import messages from './documentMessages';
import Button from '../../shared/components/Button';
import SimpleFileUpload from '@/shared/components/input/SimpleFileUpload';

const DocumentActionsButton = props => {
  const {
    document,
    suggestedAction,
    otherActions,
    intl,
    onDeleteDocument,
    onCreateTodo,
    onOpenSignatureRequestModal,
    onOpenRevokeSignatureRequestModal,
    onSendViaEmail,
    onShareItem,
    onForward,
    onSendToSharepoint,
    openInApp,
    duplicate,
    onReplaceDocument,
    onCheckinDocument,
    onCheckoutDocument,
    onDiscardCheckout,
    actionsInProgress,
  } = props;
  const canEdit =
    document.canEdit &&
    authService.hasPermission({ functionality: 'Documents', privilege: 'Update' });
  const canBeReplaced =
    document.canBeReplaced &&
    authService.hasPermission({ functionality: 'Documents', privilege: 'Update' });
  const canDelete =
    document.canRemove &&
    authService.hasPermission({ functionality: 'Documents', privilege: 'Delete' });
  const canCreateTodo = authService.hasPermission({
    functionality: 'Todo',
    privilege: 'Create',
  });
  const canForward = document.documentType === 8 || document.documentType === 23;
  const canSign =
    authService.hasPermission({ functionality: 'Relation', privilege: 'Update' }) &&
    document.canBeSigned &&
    document.signingStatus < 20 && // not signed
    document.signingStatus !== 10; // not pending

  const canRevokeSignatureRequest =
    authService.hasPermission({ functionality: 'Relation', privilege: 'Update' }) &&
    document.signingProvider !== 4 &&
    document.signingProvider !== 6 &&
    document.signingId &&
    document.signingStatus >= 10 &&
    document.signingStatus <= 12;

  const dropdownRef = useRef();

  const handleReplaceDocument = (document, files) => {
    onReplaceDocument(document, files);
    if (dropdownRef.current) {
      dropdownRef.current.handleClose();
    }
  };

  if (
    (otherActions && otherActions.length) ||
    canEdit ||
    canDelete ||
    canCreateTodo ||
    canSign ||
    canBeReplaced ||
    canRevokeSignatureRequest
  ) {
    return (
      <Dropdown
        id={`splitbutton-${document.id}`}
        className="btn-group-sm btn-group-dropdown"
        pullRight
        style={{ float: 'right', minWidth: '120px' }}
        ref={dropdownRef}
      >
        <Button
          btnClassName="action-button btn-xs"
          onClick={() => suggestedAction && suggestedAction.func(document)}
          showProgress={false}
          disabled={
            document.saving || (suggestedAction.disabled && suggestedAction.disabled(document))
          }
        >
          <span>
            {document.saving && <i className="fa fa-spinner fa-spin margin5R" />}
            {suggestedAction && suggestedAction.label}
          </span>
        </Button>
        <Dropdown.Toggle bsStyle="default" bsSize="xsmall" />
        <Dropdown.Menu>
          {otherActions &&
            otherActions.map(action => (
              <MenuItem key={action.label} onSelect={() => action.func(document)}>
                {action.icon}
                {action.label}
              </MenuItem>
            ))}
          {document.storageType !== 1 &&
            canDelete &&
            dataCacheService.hasConnectPlatform(null, 31) && (
              <MenuItem onSelect={() => onSendToSharepoint(document)}>
                <SharepointIcon className="margin5R" />
                {intl.formatMessage(messages.MoveToSharepoint)}
              </MenuItem>
            )}
          {document.storageType !== 1 && canDelete && canEdit && (
            <MenuItem onSelect={() => duplicate(document)}>
              <MdOpenInNew
                style={{ fontSize: '19px', verticalAlign: 'bottom' }}
                className="margin5R"
              />
              <>{intl.formatMessage(messages.Duplicate)}</>
            </MenuItem>
          )}
          {document.storageType === 1 && canEdit && (
            <MenuItem onSelect={() => openInApp(document)}>
              <MdOpenInNew
                style={{ fontSize: '19px', verticalAlign: 'bottom' }}
                className="margin5R"
              />
              <>{intl.formatMessage(messages.OpenInApp)}</>
            </MenuItem>
          )}
          {canForward && (
            <MenuItem onSelect={() => onForward(document)}>
              <MdForwardToInbox
                style={{ fontSize: '19px', verticalAlign: 'bottom' }}
                className="margin5R"
              />
              {intl.formatMessage(messages.Forward)}
            </MenuItem>
          )}

          {canSign && (
            <MenuItem onSelect={() => onOpenSignatureRequestModal(document)}>
              <PaperPlaneIcon className="margin5R" />
              {intl.formatMessage(messages.SendSignatureRequest)}
            </MenuItem>
          )}
          {canRevokeSignatureRequest && (
            <MenuItem onSelect={() => onOpenRevokeSignatureRequestModal(document)}>
              <UndoIcon className="margin5R" />
              {intl.formatMessage(messages.RevokeSignatureRequest)}
            </MenuItem>
          )}
          {DocumentService.canConvertToPdf(document.mime) && (
            <MenuItem onSelect={() => DocumentService.convertToPdf(document.id, document.name)}>
              <FaFilePdf className="text-danger margin5R" />
              {intl.formatMessage(messages.DownloadAsPdf)}
            </MenuItem>
          )}
          <MenuItem onSelect={() => onSendViaEmail(document, false)}>
            <MailIcon className=" margin5R" color="#333333" />
            {intl.formatMessage(messages.SendViaEmail)}
          </MenuItem>
          {DocumentService.canConvertToPdf(document.mime) && (
            <MenuItem onSelect={() => onSendViaEmail(document, true)}>
              <MailIcon className="margin5R" color="#333333" />
              {intl.formatMessage(messages.SendAsPdf)}
            </MenuItem>
          )}
          {canCreateTodo && (
            <MenuItem onSelect={() => onCreateTodo(document)}>
              <ToDoIcon /> {intl.formatMessage(messages.CreateNewToDo)}
            </MenuItem>
          )}
          <MenuItem onSelect={() => onShareItem(document)}>
            <ShareIcon className="margin5R" /> {intl.formatMessage(messages.Share)}
          </MenuItem>
          {document.storageType !== 1 &&
            canEdit &&
            document.checkedOutByUserId == null &&
            dataCacheService.hasUserConnectPlatform(46) && (
              <MenuItem
                key={intl.formatMessage(messages.CheckoutApp)}
                onSelect={() => onCheckoutDocument(document, true)}
                disabled={actionsInProgress}
              >
                <IconPencil className="margin5R" />
                {intl.formatMessage(messages.CheckoutApp)}
              </MenuItem>
            )}
          {document.storageType !== 1 &&
            canEdit &&
            document.checkedOutByUserId == null &&
            dataCacheService.hasUserConnectPlatform(46) && (
              <MenuItem
                key={intl.formatMessage(messages.CheckoutBrowser)}
                onSelect={() => onCheckoutDocument(document, false)}
                disabled={actionsInProgress}
              >
                <IconPencil className="margin5R" />
                {intl.formatMessage(messages.CheckoutBrowser)}
              </MenuItem>
            )}
          {document.storageType !== 1 &&
            canEdit &&
            document.checkedOutByUserId != null &&
            document.checkedOutByUserId == authService.getUserId() &&
            dataCacheService.hasUserConnectPlatform(46) && (
              <MenuItem
                key={intl.formatMessage(messages.ContinueEditing)}
                onSelect={() =>
                  DocumentService.getCheckedOutLink(document.id).then(link => {
                    if (!link) return;
                    window.open(link);
                  })
                }
                disabled={actionsInProgress}
              >
                <IconPencil className="margin5R" />
                {intl.formatMessage(messages.ContinueEditing)}
              </MenuItem>
            )}
          {document.storageType !== 1 &&
            canEdit &&
            document.checkedOutByUserId != null &&
            dataCacheService.hasUserConnectPlatform(46) && (
              <MenuItem
                key={intl.formatMessage(messages.Checkin)}
                onSelect={() => onCheckinDocument(document)}
                // Disabled if user is not checked out user
                disabled={
                  document.checkedOutByUserId != authService.getUserId() || actionsInProgress
                }
              >
                <SaveIcon className="margin5R" />
                {intl.formatMessage(messages.Checkin)}
              </MenuItem>
            )}
          {document.storageType !== 1 &&
            canEdit &&
            document.checkedOutByUserId != null &&
            (dataCacheService.hasUserConnectPlatform(46) ||
              authService.hasPermission({ functionality: 'CompanyOwn', privilege: 'Update' })) && (
              <MenuItem
                key={intl.formatMessage(messages.DiscardCheckoutAction)}
                onSelect={() => onDiscardCheckout(document)}
                // Disabled if user is not admin or checked out user
                disabled={
                  (document.checkedOutByUserId != authService.getUserId() &&
                    !authService.hasPermission({
                      functionality: 'CompanyOwn',
                      privilege: 'Update',
                    })) ||
                  actionsInProgress
                }
              >
                <UndoIcon className="margin5R" />
                {intl.formatMessage(messages.DiscardCheckoutAction)}
              </MenuItem>
            )}
          {canEdit && !dataCacheService.hasUserConnectPlatform(46) && (
            <MenuItem
              key={intl.formatMessage(messages.Edit)}
              onSelect={() => DocumentService.edit(document.id, intl.locale.split('-')[0])}
            >
              <IconPencil className="margin5R" />
              {intl.formatMessage(messages.Edit)}
            </MenuItem>
          )}
          {canBeReplaced && (
            <SimpleFileUpload
              onUpload={files => handleReplaceDocument(document, files)}
              accept={document.mime}
              disabled={!!document.replacing || actionsInProgress}
            >
              {({ onClick }) => (
                <MenuItem key="Replace" onSelect={onClick} disabled={!!document.replacing}>
                  {document.replacing ? (
                    <SpinnerIcon className="margin5R" />
                  ) : (
                    <ReplaceIcon className="margin5R" />
                  )}
                  {intl.formatMessage(messages.Replace)}
                </MenuItem>
              )}
            </SimpleFileUpload>
          )}
          {canDelete && (
            <MenuItem
              key={intl.formatMessage(messages.DeleteDocument)}
              onSelect={() => onDeleteDocument(document)}
            >
              <DeleteIcon className="margin5R" />
              {intl.formatMessage(messages.DeleteDocument)}
            </MenuItem>
          )}
        </Dropdown.Menu>
      </Dropdown>
    );
  }
  // Fallback
  return (
    <Button
      btnClassName="action-button btn-xs center"
      style={{ float: 'right', minWidth: '120px' }}
      onClick={() => suggestedAction && suggestedAction.func(document)}
      showProgress={false}
      disabled={document.saving}
    >
      <span>
        {document.saving && <i className="fa fa-spinner fa-spin margin5R" />}
        {suggestedAction && suggestedAction.label}
      </span>
    </Button>
  );
};

DocumentActionsButton.propTypes = {
  document: PropTypes.shape({}).isRequired,
  suggestedAction: PropTypes.shape({}).isRequired,
  otherActions: PropTypes.arrayOf(PropTypes.shape({})),
  onDeleteDocument: PropTypes.func.isRequired,
  onCreateTodo: PropTypes.func.isRequired,
  onOpenSignatureRequestModal: PropTypes.func.isRequired,
  onOpenRevokeSignatureRequestModal: PropTypes.func.isRequired,
  onShareItem: PropTypes.func.isRequired,
  onSendViaEmail: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
  onForward: PropTypes.func.isRequired,
  onSendToSharepoint: PropTypes.func.isRequired,
  openInApp: PropTypes.func.isRequired,
  duplicate: PropTypes.func.isRequired,
  onReplaceDocument: PropTypes.func.isRequired,
};
DocumentActionsButton.defaultProps = {
  otherActions: [],
};
export default injectIntl(DocumentActionsButton);
